import React, { useContext, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { FlexCol, FlexRow, TitleSmall } from '../../../common/components/v2';
import { useCaseLock } from '../../../common/caseLocking/CaseLockContext';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { ReasonsEnum } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { Aps } from '../../tasks/types/aps';
import styles from '../styles.module.scss';
import { Feedback } from './components/Feedback';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { Remarks } from './components/Remarks';
import { TaskDetailsItem } from './components/TaskDetailsItem';

export const APSDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  feedbackHandler,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  feedbackHandler: (
    taskId: number,
    sufficent: boolean,
    feedback: string,
  ) => void;
}) => {
  const [needsFeedback, setneedsFeedback] = useState(false);
  const navigate = useNavigate();
  const { flags } = useContext(FeatureFlagsContext);
  const { isApsDoctorInfoEnabled } = flags;
  const { isLockedForActions } = useCaseLock();
  const apsTask: Aps = task.taskData as Aps;
  const evidence = task.evidence;
  const evidenceReasons: string[] = apsTask.reason.map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      if (
        (needsFeedback === false && state === TaskState.COMPLETED) ||
        needsFeedback
      ) {
        setneedsFeedback(true);
        return;
      }
      taskStateHandler(task.id, state, reason);
    }
  };

  const onFeedback = async (
    sufficient: boolean,
    text: string,
    noOperation: boolean,
  ) => {
    if (noOperation) {
      setneedsFeedback(false);
      return;
    }
    setneedsFeedback(false);
    feedbackHandler(task.id, sufficient, text);
  };

  return (
    <div data-label={ElementLocators.LOCATOR_APS_SECTION}>
      <FlexRow id={task.id.toString()} className={styles.Title}>
        <FlexCol className={styles.TitleTaskIcon}>
          <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
        </FlexCol>
        <FlexCol>
          <TitleSmall>{Resource.TASK_TITLE_APS}</TitleSmall>
        </FlexCol>
        <FlexCol className={styles.CreatedDate}>
          {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
        </FlexCol>
      </FlexRow>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {needsFeedback ? (
        <Feedback show={needsFeedback} feedbackHandler={onFeedback}></Feedback>
      ) : task.sufficient != null ? (
        <Remarks
          sufficient={task.sufficient}
          feedback={task.feedback}
        ></Remarks>
      ) : (
        <></>
      )}
      <TaskDetailsItem
        leftItem={Resource.TASK_ID}
        rightItem={task ? task.id.toString() : Resource.NA}
      />
      {isApsDoctorInfoEnabled ? (
        <>
          <TaskDetailsItem
            leftItem={Resource.ORDERING_STATUS}
            rightItem={
              <FlexRow style={{ alignItems: 'center', gap: '8px' }}>
                <span>
                  {task.state === TaskState.ORDERED ? 'Ordered' : 'Not ordered'}
                </span>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigate(0);
                  }}
                  aria-label="Refresh page"
                  style={{ padding: '4px' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </FlexRow>
            }
          />
          {apsTask?.latestUwaInstructions && (
            <TaskDetailsItem
              leftItem={Resource.EXAMONE_STATUS_UPDATES}
              rightItem={apsTask.latestUwaInstructions}
            />
          )}
          {apsTask?.doctorInfo &&
          typeof apsTask.doctorInfo === 'string' &&
          apsTask.doctorInfo.trim().length > 0 ? (
            // Show doctor info if it exists and is not empty (task is ordered)
            <TaskDetailsItem
              leftItem={Resource.DOCTOR_INFO}
              rightItem={apsTask.doctorInfo}
            />
          ) : (
            // Show link to update doctor info, unless the case is locked to another UW
            <TaskDetailsItem
              leftItem={Resource.DOCTOR_INFO}
              rightItem={
                isLockedForActions ? (
                  Resource.NA
                ) : (
                  <Link
                    target="_blank"
                    to={`aps-details/${task.id}?EHRInsufficient=${apsTask.reason.includes(ReasonsEnum.EHR_INSUFFICIENT)}`}
                    className={styles.OrderButton}
                  >
                    {Resource.UPDATE_DOCTOR_INFO}
                  </Link>
                )
              }
            />
          )}
        </>
      ) : (
        <TaskDetailsItem
          leftItem={Resource.DOCTOR_INFO}
          rightItem={apsTask ? apsTask.doctorInfo : Resource.NA}
        />
      )}
      {evidenceExists(evidence) && evidence && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}
      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED &&
        !evidenceExists(evidence) && (
          <FileUpload
            leftItem={Resource.FILES}
            selectedFiles={onFileSelection}
            type={DocumentType.APS}
            ongoingUpload={task.ongoingUpload?.[DocumentType.APS]}
          />
        )}
      {(evidenceReasons?.length > 0 || apsTask?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {apsTask?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={apsTask.reasonDescription}
            />
          )}
        </>
      )}
    </div>
  );
};
